// src/components/Pages/MainModulesPanel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { Models } from "../Seguridad/Utils/ModelsSeguridad";
import { getSessionUserId } from "../../utils/SessionUser";
import './../../style/MobileStyles.css';

// Íconos para cada módulo
const moduleIcons = {
  "lms": "🎓",
  "car-booking": "🚗",
  "rh": "👥",
  "intranet": "🏢",
  "seguridad": "🔒",
  "conciliacion": "📊",
  "reportes": "📈",
  "config": "⚙️",
  "sistema": "🛠️"
};

const MODULES = [
  {
    id: "lms",
    title: "LMS",
    description: "Gestiona cursos, evaluaciones y certificados.",
    color: "green",
    path: "/lms"
  },
  {
    id: "car-booking",
    title: "Car Booking",
    description: "Reservas de viajes, choferes y vehículos.",
    color: "blue",
    path: "/car-booking"
  },
  {
    id: "rh",
    title: "RH",
    description: "Gestión de personal y estructura organizacional.",
    color: "rose",
    path: "/rh"
  },
  {
    id: "intranet",
    title: "Intranet",
    description: "Comunicación interna, noticias, avisos, encuestas y documentos.",
    color: "indigo",
    path: "/intranet"
  },
  {
    id: "seguridad",
    title: "Seguridad",
    description: "Usuarios, roles, permisos y accesos.",
    color: "purple",
    path: "/seguridad"
  },
  {
    id: "conciliacion",
    title: "Conciliación Contable",
    description: "Layouts, cargas y conciliaciones.",
    color: "orange",
    path: "/conciliacion"
  },
  {
    id: "reportes",
    title: "Reportes",
    description: "Indicadores y reportes globales del ERP.",
    color: "yellow",
    path: "/reportes"
  },
  {
    id: "config",
    title: "Configuración",
    description: "Parámetros generales del sistema.",
    color: "cyan",
    path: "/configuracion"
  },
  {
    id: "sistema",
    title: "Sistema",
    description: "Configuraciones core y temas del sistema.",
    color: "slate",
    path: "/sistema"
  }
];

export default function MainModulesPanel() {
  const navigate = useNavigate();
  const [sessionUserId] = useState(() => getSessionUserId());
  const [visibleModules, setVisibleModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClick = (path) => {
    const target = path.startsWith("/menu") ? path : `/menu${path}`;
    navigate(target);
  };

  useEffect(() => {
    let isMounted = true;

    const loadRecursos = async () => {
      try {
        if (!sessionUserId) {
          setErrorMsg("No se pudo identificar al usuario en sesión. No hay módulos disponibles.");
          setVisibleModules([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        const res = await axios.get(ENDPOINTS.recursos + `/${sessionUserId}/Panel`);

        const recursos = (res?.data || []).map(Models.catRecursos || ((row) => row));
        // Filtrar módulos basados en los recursos disponibles
        const modulosDisponibles = MODULES.filter(modulo =>
          recursos.some(r => r.ruta === modulo.id)
        );

        if (isMounted) {
          setVisibleModules(modulosDisponibles);
          setErrorMsg("");
        }
      } catch (error) {
        console.error("Error al cargar recursos:", error);
        if (isMounted) {
          setErrorMsg("Error al cargar los módulos disponibles. Por favor, intente recargar la página.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRecursos();

    return () => {
      isMounted = false;
    };
  }, [sessionUserId]);

  if (loading) {
    return (
      <div className="modules-loading">
        <div>Cargando módulos...</div>
      </div>
    );
  }

  if (errorMsg) {
    return <div className="modules-error">{errorMsg}</div>;
  }

  return (
    <div className="modules-container">
      <h1>Módulos Disponibles</h1>
      <div className="modules-grid">
        {visibleModules.length > 0 ? (
          visibleModules.map((module) => (
            <div
              key={module.id}
              className={`module-card module-card--${module.color}`}
              onClick={() => handleClick(module.path)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleClick(module.path)}
            >
              <div className="module-card__title">{module.title}</div>
              <div className="module-card__description">{module.description}</div>
              <div className="module-card__icon" aria-hidden="true">
                {moduleIcons[module.id] || "📁"}
              </div>
            </div>
          ))
        ) : (
          <div className="modules-error">
            No tienes acceso a ningún módulo en este momento.
          </div>
        )}
      </div>
    </div>
  );
}